import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Paper from '@mui/material/Paper';
import { useDispatch, useSelector } from 'react-redux';
import {
  Chip,
  IconButton,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Grid,
  Typography,
  Stack,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LaunchIcon from '@mui/icons-material/Launch';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import { getStorePayouts } from '../../../redux/actions/storesActions';
import { fCurrency } from '../../../utils/formatNumber';
import { fDateShort } from '../../../utils/formatTime';
import TransitionComponent from '../TransitionComponent';

const TableCellStyled = styled(TableCell)(({ theme }) => ({
  maxWidth: '10rem',
  minWidth: '10rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: theme.spacing(2),
  '&.amount': {
    fontWeight: 500,
  },
  '&.fee': {
    color: theme.palette.text.secondary,
  },
}));

const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: '1px solid #919EAB3D',
}));

const TableContainerStyled = styled(Paper)(({ theme }) => ({
  border: '1px solid #919EAB3D',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  width: '100%',
  maxWidth: '100%',
}));

const PanelContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
}));

function PayoutsList() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['payouts']);
  const selectedStore = useSelector((state) => state.merchant.selectedStore);
  const payouts = useSelector((state) => state.merchant.payouts);
  const loadingPayouts = useSelector((state) => state.merchant.loadingPayouts);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openBankModal, setOpenBankModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [filters, setFilters] = useState({
    reference: '',
    dateFrom: '',
    dateTo: '',
  });

  const [filteredPayouts, setFilteredPayouts] = useState([]);

  useEffect(() => {
    if (selectedStore?.id_store) {
      dispatch(getStorePayouts(selectedStore.id_store));
    }
  }, [selectedStore]);

  useEffect(() => {
    if (payouts) {
      const filtered = payouts.filter((payout) => {
        const matchesReference = !filters.reference || payout.reference.toLowerCase().includes(filters.reference.toLowerCase());

        // Convert payout date to start of day for consistent comparison
        const payoutDate = new Date(payout.paid_date);
        payoutDate.setHours(0, 0, 0, 0);

        // Convert filter dates to start of day for consistent comparison
        const dateFrom = filters.dateFrom ? new Date(filters.dateFrom) : null;
        const dateTo = filters.dateTo ? new Date(filters.dateTo) : null;
        if (dateFrom) dateFrom.setHours(0, 0, 0, 0);
        if (dateTo) dateTo.setHours(23, 59, 59, 999);

        const matchesDateFrom = !dateFrom || payoutDate >= dateFrom;
        const matchesDateTo = !dateTo || payoutDate <= dateTo;

        return matchesReference && matchesDateFrom && matchesDateTo;
      });
      setFilteredPayouts(filtered);
    }
  }, [payouts, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event, payout) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayout(payout);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBankDetails = () => {
    setOpenBankModal(true);
    handleMenuClose();
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusTranslation = (status) => {
    switch (status) {
      case 'pending':
        return t('status.pending');
      case 'processing':
        return t('status.processing');
      case 'paid':
        return t('status.paid');
      case 'cancelled':
        return t('status.cancelled');
      default:
        return status;
    }
  };

  return (
    <TransitionComponent>
      <PanelContainer>
        <Box sx={{ my: 3 }}>
          <Typography variant="h4" sx={{ mb: 1 }}>
            {t('Payouts')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('View and manage your payouts')}
          </Typography>
        </Box>

        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          <FilterContainer elevation={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('Reference')}
                  name="reference"
                  value={filters.reference}
                  onChange={handleFilterChange}
                  size="small"
                  sx={{ backgroundColor: 'background.paper' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label={t('Date From')}
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ backgroundColor: 'background.paper' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label={t('Date To')}
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ backgroundColor: 'background.paper' }}
                />
              </Grid>
            </Grid>
          </FilterContainer>

          <TableContainerStyled elevation={0}>
            <Box sx={{ width: '100%', position: 'relative' }}>
              <Table
                sx={{
                  width: '100%',
                  tableLayout: 'fixed',
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCellStyled width="20%">{t('columns.reference')}</TableCellStyled>
                    <TableCellStyled width="15%">{t('columns.date')}</TableCellStyled>
                    <TableCellStyled width="20%" align="right">
                      {t('columns.amount')}
                    </TableCellStyled>
                    <TableCellStyled width="15%" align="right">
                      {t('columns.fee')}
                    </TableCellStyled>
                    <TableCellStyled width="15%">{t('columns.status')}</TableCellStyled>
                    <TableCellStyled width="15%" align="center">
                      {t('columns.actions')}
                    </TableCellStyled>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingPayouts ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : filteredPayouts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <Stack spacing={1} alignItems="center">
                          <InfoIcon color="info" />
                          <Link href="/config" variant="body2" color="primary">
                            <Typography variant="body1" color="text.secondary">
                              {t('no-payouts')}
                            </Typography>
                          </Link>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPayouts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((payout) => (
                      <TableRow key={payout.id_payout} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCellStyled>{payout.reference}</TableCellStyled>
                        <TableCellStyled>{fDateShort(payout.paid_date)}</TableCellStyled>
                        <TableCellStyled align="right" className="amount">
                          {fCurrency(payout.total_amount, selectedStore?.currency)}
                        </TableCellStyled>
                        <TableCellStyled align="right" className="fee">
                          {fCurrency(payout.payout_fee, selectedStore?.currency)}
                        </TableCellStyled>
                        <TableCellStyled>
                          <Chip
                            label={getStatusTranslation(payout.status)}
                            color={getStatusColor(payout.status)}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              minWidth: 80,
                              justifyContent: 'center',
                            }}
                          />
                        </TableCellStyled>
                        <TableCellStyled align="center">
                          <IconButton
                            size="small"
                            onClick={(event) => handleMenuClick(event, payout)}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCellStyled>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
            {!loadingPayouts && filteredPayouts.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredPayouts.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ borderTop: '1px solid #919EAB3D' }}
              />
            )}
          </TableContainerStyled>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={handleBankDetails}>
              <ListItemIcon>
                <AccountBalanceIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('Bank destination')}</ListItemText>
            </MenuItem>
            {selectedPayout?.orders?.length > 0 ? (
              <MenuItem component={Link} to={`/orders?payout=${selectedPayout.id_payout}`} onClick={handleMenuClose}>
                <ListItemIcon>
                  <ReceiptLongIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('View Orders')}</ListItemText>
              </MenuItem>
            ) : (
              selectedPayout?.external_reference && (
                <MenuItem component="a" href={selectedPayout.external_reference} target="_blank" rel="noopener noreferrer" onClick={handleMenuClose}>
                  <ListItemIcon>
                    <LaunchIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('View External Orders')}</ListItemText>
                </MenuItem>
              )
            )}
          </Menu>

          <Dialog open={openBankModal} onClose={() => setOpenBankModal(false)}>
            <DialogTitle>{t('bank-account-details')}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 2 }}>
                <Typography>
                  <strong>{t('account-name')}</strong>
                  {selectedPayout?.account?.account_name || t('no-account-name')}
                </Typography>
                <Typography>
                  <strong>{t('account-number')}</strong>
                  {selectedPayout?.account?.account_number || t('no-account-number')}
                </Typography>
              </Stack>
            </DialogContent>
          </Dialog>
        </Box>
      </PanelContainer>
    </TransitionComponent>
  );
}

export default PayoutsList;
